import React, { useCallback, useRef } from 'react';
import { IconButton, InputAdornment, Typography } from '@mui/material';
import SearchOutlined from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { BasicInput } from '@payer-admin-ui/components-v2/src/inputs/BasicInput';
import { BasicIcon, IconType } from '@payer-admin-ui/components-v2/src/icons/BasicIcon';
import { Tooltip } from '@payer-admin-ui/components-v2/src/utils/Tooltip';
import { useKeyPress } from '../hooks/useKeyPress';

export type SearchBarProps = {
  value: string;
  setValue: (input: string) => void;
  onClick: (input: string) => void;
  placeholder?: string;
  tooltipItems?: string[];
};
const SearchBar = ({ value, setValue, onClick, placeholder, tooltipItems }: SearchBarProps) => {
  const { t } = useTranslation();

  const searchInputRef = useRef<HTMLInputElement | undefined>();

  const handlePerformSearch = useCallback(() => {
    if (onClick && searchInputRef.current) {
      onClick(searchInputRef.current.value);
    }
  }, [onClick]);

  const handleEnterSearch = useCallback(() => {
    document.activeElement === searchInputRef.current && handlePerformSearch();
  }, [handlePerformSearch]);

  useKeyPress('Enter', handleEnterSearch);

  const tooltipText = tooltipItems && (
    <>
      <Typography>Search on: </Typography>
      {tooltipItems.map(item => (
        <Typography key={item}>- {item}</Typography>
      ))}
    </>
  );

  return (
    <Tooltip title={tooltipText} arrow>
      <BasicInput
        variant="outlined"
        size="small"
        placeholder={placeholder || t('components.searchbar.search')}
        value={value}
        sx={{
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: theme => theme.palette.grey.A400,
            borderWidth: '1px',
          },
          '.MuiInputAdornment-root': {
            ml: 0,
            '& svg': {
              fontSize: '1.25rem',
            },
          },
        }}
        InputProps={{
          sx: {
            borderRadius: '5px',
            backgroundColor: 'white',
            maxWidth: '250px',
            width: '250px',
            minWidth: '250px',
          },
          endAdornment: (
            <>
              {value && (
                <InputAdornment position="end" onClick={() => setValue('')}>
                  <IconButton size="small">
                    <BasicIcon type={IconType.CLEAR} />
                  </IconButton>
                </InputAdornment>
              )}
              <InputAdornment position="end" onClick={handlePerformSearch}>
                <IconButton size="small">
                  <SearchOutlined />
                </IconButton>
              </InputAdornment>
            </>
          ),
        }}
        onChange={event => setValue(event.target.value)}
        inputRef={searchInputRef}
      />
    </Tooltip>
  );
};

export { SearchBar };
